import { createUseTranslationMap } from '@vend/react-utilities'
import { TFunction, useT } from '@transifex/react'
import { useCallback } from 'react'
import { LSPAY_ERROR } from '../helpers/lspayConstants'
import { LSPAYErrorMessage } from '../helpers/lspay.types'

let lspayErrorHeaders: Record<string, string>
let lspayErrorBodies: Record<string, string>
const TContextHeader = 'Payment error header'
const TContextBody = 'Payment error body'

const buildErrorBodyMap = (t: TFunction) => {
  lspayErrorBodies = {
    [LSPAY_ERROR.DEFAULT_PAYMENT]: t(
      'You can retry with the same payment type or choose another payment type to complete this sale. If this keeps happening please contact Lightspeed Retail Support.',
      { _context: TContextBody }
    ),
    [LSPAY_ERROR.REMOVE_PREVIOUS_CARD]: t(
      'Cannot start transaction with card inserted from previous transaction, remove card and try again.',
      { _context: TContextBody }
    ),
    [LSPAY_ERROR.READER_BACKGROUND]: t(
      'Cannot collect payment while terminal application is in the background. The device may be busy performing updates or changing settings.',
      { _context: TContextBody }
    ),
    [LSPAY_ERROR.BROWSER_CONNECTIVITY]: t(
      'Check and ensure your browser is connected to the internet and has a stable connection.',
      { _context: TContextBody }
    ),
    [LSPAY_ERROR.TERMINAL_CONNECTIVITY]: t(
      'Check and ensure your terminal is connected to the internet and has a stable connection.',
      { _context: TContextBody }
    ),
    [LSPAY_ERROR.TERMINAL_DISCONNECTED]: t(
      'Check and ensure your terminal is connected to the internet and has a stable connection.',
      { _context: TContextBody }
    ),
    [LSPAY_ERROR.CANCEL_PAYMENT]: t(
      'You can try with the same payment type or choose another payment type to complete this sale.',
      { _context: TContextBody }
    ),
    [LSPAY_ERROR.CANCEL_PAYMENT_WITH_INTENT_CANCELLED]: t(
      'Ensure your terminal is connected to stable internet and retry the payment.',
      { _context: TContextBody }
    ),
    [LSPAY_ERROR.DEFAULT_REFUND]: t(
      'You can retry with the same payment type or choose another payment type to complete this refund. If this keeps happening please contact Lightspeed Retail Support.',
      { _context: TContextBody }
    ),
    [LSPAY_ERROR.CANCEL_REFUND]: t(
      'You can try with the same payment type or choose another payment type to complete this refund.',
      { _context: TContextBody }
    ),
    [LSPAY_ERROR.CANCEL_REFUND_WITH_INTENT_CANCELLED]: t(
      'Check and ensure your terminal is connected to the internet and has a stable connection.',
      { _context: TContextBody }
    ),
    [LSPAY_ERROR.UNREF_REFUND_NOT_ENABLED]: t(
      'Your Lightspeed Payments account isn’t currently set up to process refunds that aren’t directly linked to the original payment. You can choose another payment type to process the refund or contract Lightspeed Retail Support to have this feature enabled on your account.',
      { _context: TContextBody }
    ),
    [LSPAY_ERROR.CARD_EXPIRED]: t(
      'You can try with a different card or choose another payment type.',
      { _context: TContextBody }
    ),
    [LSPAY_ERROR.CARD_DECLINED]: t(
      'You can try with a different card or choose another payment type.',
      { _context: TContextBody }
    ),
    [LSPAY_ERROR.CARD_DECLINED_INSUFFICIENT]: t(
      'You can try with a different card or choose another payment type.',
      { _context: TContextBody }
    ),
    [LSPAY_ERROR.CARD_DECLINED_INCORRECT_PIN]: t(
      'You can retry or choose another payment type to complete this sale.',
      { _context: TContextBody }
    ),
    [LSPAY_ERROR.CARD_DECLINED_INVALID_ACCOUNT]: t(
      'You can retry or choose another payment type to complete this sale.',
      { _context: TContextBody }
    ),
    [LSPAY_ERROR.CARD_DECLINED_INVALID_AMOUNT]: t(
      'You can retry or choose another payment type to complete this sale.',
      { _context: TContextBody }
    ),
    [LSPAY_ERROR.CARD_DECLINED_NOT_ALLOWED]: t(
      'You can retry with a different card or choose another payment type to complete this sale.',
      { _context: TContextBody }
    ),
    [LSPAY_ERROR.CARD_DECLINED_PIN_TRY_EXCEEDED]: t(
      'You can retry with a different card or choose another payment type to complete this sale.',
      { _context: TContextBody }
    ),
    [LSPAY_ERROR.PAIRING_MISSING]: t(
      'To make a payment, pair this register with a terminal in your payment type setup. To complete this sale choose another payment type.',
      { _context: TContextBody }
    ),
    [LSPAY_ERROR.PAIRING_MISSING_REFUND]: t(
      'To make a refund, pair this register with a terminal in your payment type setup. To complete this refund choose another payment type.',
      { _context: TContextBody }
    ),
    [LSPAY_ERROR.CARD_DECLINED_REFUND]: t(
      'You can try with a different card or choose another payment type to complete this refund.',
      { _context: TContextBody }
    ),
    [LSPAY_ERROR.CARD_EXPIRED_REFUND]: t(
      'You can try with a different card or choose another payment type to complete this refund.',
      { _context: TContextBody }
    ),
    [LSPAY_ERROR.CARD_DECLINED_INCORRECT_PIN_REFUND]: t(
      'You can retry or choose another payment type to complete this refund.',
      { _context: TContextBody }
    ),
    [LSPAY_ERROR.CARD_DECLINED_PIN_TRY_EXCEEDED_REFUND]: t(
      'You can retry with a different card or choose another payment type to complete this refund.',
      { _context: TContextBody }
    ),
    [LSPAY_ERROR.CARD_DECLINED_NOT_ALLOWED_REFUND]: t(
      'You can retry with a different card or choose another payment type to complete this refund.',
      { _context: TContextBody }
    ),
    [LSPAY_ERROR.CARD_DECLINED_INVALID_ACCOUNT_REFUND]: t(
      'You can retry or choose another payment type to complete this refund.',
      { _context: TContextBody }
    ),
  }
}

const buildErrorHeaderMap = (t: TFunction) => {
  lspayErrorHeaders = {
    [LSPAY_ERROR.DEFAULT_PAYMENT]: t(
      "Something went wrong and this payment hasn't been taken.",
      {
        _context: TContextHeader,
      }
    ),
    [LSPAY_ERROR.REMOVE_PREVIOUS_CARD]: t(
      "Something went wrong and this payment hasn't been taken.",
      {
        _context: TContextHeader,
      }
    ),
    [LSPAY_ERROR.READER_BACKGROUND]: t(
      "Something went wrong and this payment hasn't been taken.",
      {
        _context: TContextHeader,
      }
    ),
    [LSPAY_ERROR.BROWSER_CONNECTIVITY]: t(
      "Something went wrong and this payment hasn't been taken.",
      {
        _context: TContextHeader,
      }
    ),
    [LSPAY_ERROR.TERMINAL_CONNECTIVITY]: t(
      "Something went wrong and this payment hasn't been taken.",
      {
        _context: TContextHeader,
      }
    ),
    [LSPAY_ERROR.TERMINAL_DISCONNECTED]: t(
      'The connection to your terminal was lost.',
      {
        _context: TContextHeader,
      }
    ),
    [LSPAY_ERROR.CANCEL_PAYMENT]: t('This payment has been cancelled.', {
      _context: TContextHeader,
    }),
    [LSPAY_ERROR.CANCEL_PAYMENT_WITH_INTENT_CANCELLED]: t(
      'This payment was not processed because of a connection issue.',
      { _context: TContextHeader }
    ),
    [LSPAY_ERROR.DEFAULT_REFUND]: t(
      'Something went wrong and this refund has not been processed.',
      {
        _context: TContextHeader,
      }
    ),
    [LSPAY_ERROR.CANCEL_REFUND]: t('This refund has been cancelled.', {
      _context: TContextHeader,
    }),
    [LSPAY_ERROR.CANCEL_REFUND_WITH_INTENT_CANCELLED]: t(
      `The refund can't be processed because of a connection issue`,
      { _context: TContextHeader }
    ),
    [LSPAY_ERROR.UNREF_REFUND_NOT_ENABLED]: t('The refund can’t be processed', {
      _context: TContextHeader,
    }),
    [LSPAY_ERROR.CARD_EXPIRED]: t(
      'This payment has been declined because the customer used an expired card.',
      {
        _context: TContextHeader,
      }
    ),
    [LSPAY_ERROR.CARD_DECLINED]: t('The payment has been declined.', {
      _context: TContextHeader,
    }),
    [LSPAY_ERROR.CARD_DECLINED_INSUFFICIENT]: t(
      "The payment has been declined because the customer's card has insufficient funds.",
      { _context: TContextHeader }
    ),
    [LSPAY_ERROR.CARD_DECLINED_INCORRECT_PIN]: t(
      'The payment has been declined (incorrect PIN).',
      {
        _context: TContextHeader,
      }
    ),
    [LSPAY_ERROR.CARD_DECLINED_INVALID_ACCOUNT]: t(
      'The payment has been declined (invalid account).',
      {
        _context: TContextHeader,
      }
    ),
    [LSPAY_ERROR.CARD_DECLINED_INVALID_AMOUNT]: t(
      'The payment has been declined (invalid amount).',
      {
        _context: TContextHeader,
      }
    ),
    [LSPAY_ERROR.CARD_DECLINED_NOT_ALLOWED]: t(
      'The payment has been declined (this card does not support this type of purchase).',
      { _context: TContextHeader }
    ),
    [LSPAY_ERROR.CARD_DECLINED_PIN_TRY_EXCEEDED]: t(
      'The payment has been declined (allowable number of PIN tries exceeded).',
      { _context: TContextHeader }
    ),
    [LSPAY_ERROR.PAIRING_MISSING]: t(
      'The register is not paired with a terminal.',
      {
        _context: TContextHeader,
      }
    ),
    [LSPAY_ERROR.PAIRING_MISSING_REFUND]: t(
      'The register is not paired with a terminal.',
      {
        _context: TContextHeader,
      }
    ),
    [LSPAY_ERROR.CARD_DECLINED_REFUND]: t('The refund has been declined.', {
      _context: TContextHeader,
    }),
    [LSPAY_ERROR.CARD_EXPIRED_REFUND]: t(
      'The refund has been declined because the customer used an expired card.',
      {
        _context: TContextHeader,
      }
    ),
    [LSPAY_ERROR.CARD_DECLINED_INCORRECT_PIN_REFUND]: t(
      'The refund has been declined (incorrect PIN)',
      {
        _context: TContextHeader,
      }
    ),
    [LSPAY_ERROR.CARD_DECLINED_PIN_TRY_EXCEEDED_REFUND]: t(
      'The refund has been declined (allowable number of PIN tries exceeded)',
      { _context: TContextHeader }
    ),
    [LSPAY_ERROR.CARD_DECLINED_NOT_ALLOWED_REFUND]: t(
      'The refund has been declined (this card does not support this type of purchase).',
      { _context: TContextHeader }
    ),
    [LSPAY_ERROR.CARD_DECLINED_INVALID_ACCOUNT_REFUND]: t(
      'The refund has been declined (invalid account)',
      {
        _context: TContextHeader,
      }
    ),
  }
}

const useErrorHeaderMap = createUseTranslationMap(
  buildErrorHeaderMap,
  (key: string) => lspayErrorHeaders[key]
)

const useErrorBodyMap = createUseTranslationMap(
  buildErrorBodyMap,
  (key: string) => lspayErrorBodies[key]
)

export const useErrorMessages = () => {
  const t = useT()
  const getHeader = useErrorHeaderMap()
  const getBody = useErrorBodyMap()

  const getErrorMessages = useCallback(
    (
      errorCode: LSPAY_ERROR,
      isCardSpecificDecline?: boolean
    ): LSPAYErrorMessage => {
      return {
        header: getHeader(errorCode.toString()) || '',
        body: getBody(errorCode.toString()) || '',
        ...(isCardSpecificDecline
          ? {
              primaryAction: t('Use another card', {
                _context: 'Payment error action to suggest using another card',
              }),
            }
          : {}),
      }
    },
    [getHeader, getBody, t]
  )

  return getErrorMessages
}
